import type { Island } from '../../../islands/island.class';
import type { Panel } from '../../../panels/panel.class';
import type { Size } from '../../../../types';
import { computeAutoResizeWidthCfn } from '../../../islands/functions/auto-resize-width.cfn';
import { getRequiredPanelWidthCfn } from '../../../panels/functions/panel-space.cfn';
import { RearrangeRefusedError, RearrangeGrowthOptions, IslandResize } from './rearrange.types';

export interface PanelGrowthPrediction {
	/** `getRequiredPanelWidthCfn` of the hypothetical columns */
	requiredWidth: number;
	fits: boolean;
	currentWidth: number;
	/** Width the island would be sized to (equals currentWidth when it fits) */
	finalWidth: number;
}

/**
 * What an island would have to do to hold `groupWidths` on `panel`. Pure with
 * respect to the model: nothing is written.
 */
export const predictPanelGrowthCfn = (island: Island, panel: Panel, groupWidths: Array<number>): PanelGrowthPrediction => {
	const currentWidth = (island.size as Size).width;
	const overlapTotal = panel.overlapWidth;
	const requiredWidth = getRequiredPanelWidthCfn(groupWidths, overlapTotal);
	const { fits, finalWidth } = computeAutoResizeWidthCfn({
		groupWidths,
		overlapTotal,
		currentWidth,
		minimumWidth: requiredWidth,
		minimumSizeWidth: island.minimumSize?.width ?? null,
		growOnly: true
	});
	return { requiredWidth, fits, currentWidth, finalWidth };
};

/**
 * Turns a prediction into either "no resize", a resize, or a refusal — the one
 * rule set the drag preview, the drop and the tools all share.
 */
export const resolveGrowthCfn = (island: Island, prediction: PanelGrowthPrediction, options: RearrangeGrowthOptions = {}): IslandResize | null => {
	if (prediction.fits) return null;

	const growable = options.growable ?? !island.sizeLocked;
	if (!growable) {
		throw new RearrangeRefusedError('locked-island-no-room', `Island '${island.id}' cannot grow and has no room (needs ${Math.ceil(prediction.requiredWidth)}", has ${prediction.currentWidth}").`);
	}

	const maxWidth = options.maxWidth ?? null;
	if (maxWidth !== null && prediction.finalWidth > maxWidth) {
		throw new RearrangeRefusedError('exceeds-max-width', `Island '${island.id}' would have to grow to ${prediction.finalWidth}", past the ${maxWidth}" limit.`);
	}

	return { islandId: island.id, fromWidthIn: prediction.currentWidth, toWidthIn: prediction.finalWidth };
};

/** Widths of every column on a panel except the ones listed, plus the ones being added. */
export const hypotheticalGroupWidthsCfn = (panel: Panel, exceptGroupIds: Array<string>, addWidths: Array<number>): Array<number> => [
	...panel.panelItemGroups.children.filter((g) => !exceptGroupIds.includes(g.id)).map((g) => g.maxOutsideWidth),
	...addWidths
];
